<template>
    <div>
		<div class="content">
			<h1>{{ lang('Users') }}</h1>
		</div>

        <div class="level">
            <div class="level-left">
                <b-field grouped>
                    <b-field>
                        <b-checkbox v-model="selectedRoles" @input="onFilterChange" v-for="role in roles" :key="role" :native-value="role">
                            {{ role }}
                        </b-checkbox>
                    </b-field>
                </b-field>
            </div>

            <div class="level-right">
                <b-field>
                    <b-input
                        v-model="keyword"
                        :placeholder="lang('Search') + '...'"
                        type="search"
                        icon-pack="fa"
                        icon="search"
                        @input="onSearch">
                    </b-input>
                    <p class="control">
                        <button class="button is-primary" @click="onSearch">{{ lang('Search') }}</button>
                    </p>
                </b-field>
            </div>
        </div>

		<b-table
            :data="data"
            :loading="loading"

            :paginated="total > 20 || page > 1"
            backend-pagination
            :total="total"
            :per-page="perPage"
            @page-change="onPageChange"

            backend-sorting
            :default-sort-direction="defaultSortOrder"
            :default-sort="[sortField, sortOrder]"
            @sort="onSort"

			detailed
        	detail-key="id"
		>

            <template slot-scope="props">
				<b-table-column field="role" :label="lang('Role')" sortable>
					{{ props.row.role }}
				</b-table-column>

                <b-table-column field="name" :label="lang('Full Name')" sortable>
                    {{ props.row.name }}
                </b-table-column>

				<b-table-column field="email" :label="lang('Email')" sortable>
                    {{ props.row.email }}
                </b-table-column>

				<b-table-column field="event_notifications" :label="lang('Event Notifications')" sortable>
                    {{ props.row.event_notifications }}
                </b-table-column>

				<b-table-column field="created_at" :label="lang('Registration Date')" sortable>
                    {{ props.row.created_at }}
                </b-table-column>
            </template>

			<template slot="detail" slot-scope="props">
				<b-field grouped>
			        <b-field :label="lang('Role')">
			            <b-select v-model="props.row.role">
			                <option value="Admin">Admin</option>
			                <option value="Trainer">Trainer</option>
			                <option value="Hallenöffner">Hallenöffner</option>
			                <option value="Mitglied">Mitglied</option>
			                <option value="KMMS">KMMS</option>
			                <option value="User">User</option>
			            </b-select>
			        </b-field>

			        <b-field :label="lang('Full Name')" expanded>
			            <b-input v-model="props.row.name" :class="{'is-danger': formErrors.name}"></b-input>
			        </b-field>

			        <b-field :label="lang('Email')" expanded>
			            <b-input v-model="props.row.email" :class="{'is-danger': formErrors.email}"></b-input>
			        </b-field>
			    </b-field>

				<p class="control">
					<button class="button is-primary" @click="save(props.row)">{{ lang('Save') }}</button>
                    <button class="button is-danger" @click="remove(props.row)">{{ lang('Delete') }}</button>
				</p>

				<p v-show="Object.keys(formErrors).length" v-for="(errors, field) in formErrors" :key="field" class="help is-danger">
					<template v-for="error in errors">
						{{ error }}
					</template>
				</p>
	        </template>

			<template slot="bottom-left">
				<b-field grouped group-multiline>
		            <b-select v-model="perPage" @input="onPerPageChange">
		                <option value="20">20 {{ lang('per page') }}</option>
		                <option value="50">50 {{ lang('per page') }}</option>
		                <option value="100">100 {{ lang('per page') }}</option>
		            </b-select>
		        </b-field>
			</template>
        </b-table>
    </div>
</template>

<script>
    export default {
		name: 'Users',
        data() {
            return {
                data: [],
				formErrors: {},
                total: 0,
                loading: false,
                sortField: 'created_at',
                sortOrder: 'desc',
                defaultSortOrder: 'asc',
                roles: ["Trainer", "Hallenöffner", "Admin", "Mitglied", "KMMS", "User"],
                selectedRoles: [],
                page: 1,
                perPage: 20,
                keyword: null,
            }
        },
        methods: {
            /*
             * Load async data
             */
            loadAsyncData() {
				let self = this;
                this.loading = true;

                axios.get(`/users`, {
                    params: {
                        keyword: this.keyword,
                        roles: this.selectedRoles,
                        sort_by: this.sortField,
                        sort_dir: this.sortOrder,
                        page: this.page,
                        per_page: this.perPage,
                    }
                })
                .then(function (response) {
					self.data = [];
                    let currentTotal = response.data.total;
                    if (currentTotal / self.perPage > 1000) {
                        currentTotal = self.perPage * 1000;
                    }
                    self.total = currentTotal;
                    response.data.data.forEach((item) => self.data.push(item));
                    self.loading = false;
                }).catch(function (error) {
					self.data = [];
                    self.total = 0;
                    self.loading = false;
					if (error.response.status == 401) { // unauthorized
						self.logout();
					} else {
						self.alertCustomError(error.response.data.message);
					}
				});
            },
            /*
             * Handle filter-change event
             */
            onFilterChange() {
                this.loadAsyncData()
            },
            /*
             * Handle search event
             */
            onSearch: _.debounce(function (e) {
                this.loadAsyncData();
            }, 500),
            /*
             * Handle page-change event
             */
            onPageChange(page) {
                this.page = page
                this.loadAsyncData()
            },
			/*
             * Handle per page-change event
             */
            onPerPageChange(perPage) {
                this.perPage = perPage
                this.loadAsyncData()
            },
            /*
             * Handle sort event
             */
            onSort(field, order) {
                this.sortField = field
                this.sortOrder = order
                this.loadAsyncData()
            },
            /*
             * Type style in relation to the value
             */
            type(value) {
                const number = parseFloat(value)
                if (number < 6) {
                    return 'is-danger'
                } else if (number >= 6 && number < 8) {
                    return 'is-warning'
                } else if (number >= 8) {
                    return 'is-success'
                }
            },
			alertCustomError(message) {
				this.$buefy.dialog.alert({
					title: 'Error',
					message: message,
					type: 'is-danger',
					hasIcon: true,
					icon: 'times-circle'
				})
			},
			logout() {
				window.location.href = '/logout';
			},
			save(user) {
				var self = this;

				axios.patch(`/users/${user.id}`, user)
				.then(function (response) {
					self.formErrors = {};
					self.$buefy.toast.open({
						message: 'Gespeichert! Alles ist gut.',
						type: 'is-success',
						position: 'is-top-right'
					});
				}).catch(function (error) {
					if (error.response.status == 422 && error.response.data.errors) {
						self.formErrors = error.response.data.errors;
						self.showEventForm = true;
					} else if (error.response.status == 401) { // unauthorized
						self.logout();
					} else {
						self.alertCustomError(error.response.data.message);
					}
				});
			},
			remove(user) {
                var self = this;

				axios.delete(`/users/${user.id}`)
				.then(function (response) {
					self.formErrors = {};
					self.$buefy.toast.open({
						message: 'Gelöscht! Alles ist gut.',
						type: 'is-success',
						position: 'is-top-right'
                    });
                    Vue.delete(self.data, _.findIndex(self.data, ['id', user.id]));
				}).catch(function (error) {
					if (error.response.status == 422 && error.response.data.errors) {
						self.formErrors = error.response.data.errors;
						self.showEventForm = true;
					} else if (error.response.status == 401) { // unauthorized
						self.logout();
					} else {
						self.alertCustomError(error.response.data.message);
					}
				});
			}
        },
        filters: {
            /**
             * Filter to truncate string, accepts a length parameter
             */
            truncate(value, length) {
                return value.length > length
                    ? value.substr(0, length) + '...'
                    : value
            }
        },
        created() {
            this.selectedRoles = this.roles;
        },
        mounted() {
            this.loadAsyncData()
        },
    }
</script>
